import React, { useState, useEffect, useCallback } from 'react';
import { Instagram, Youtube, Twitter, Facebook, Linkedin, Link as LinkIcon, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import api from '../../api';

const SocialMediaManagement = ({ userId }) => {
  const [socialAccounts, setSocialAccounts] = useState({
    instagram: { connected: false, username: '', followers: 0, url: '' },
    youtube: { connected: false, username: '', subscribers: 0, url: '' },
    twitter: { connected: false, username: '', followers: 0, url: '' },
    facebook: { connected: false, username: '', followers: 0, url: '' },
    linkedin: { connected: false, username: '', connections: 0, url: '' }
  });

  const [isLoading, setIsLoading] = useState(true);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [oauthStatus, setOauthStatus] = useState({});

  const fetchSocialAccounts = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.get(`/creators/${userId}/social-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setSocialAccounts(response.data);
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchSocialAccounts();
    fetchOAuthStatus();

    // Listen for OAuth success and error messages from popup
    const handleMessage = (event) => {
      if (event.data.type === 'oauth-success') {
        setSuccess(`${event.data.platform} connected successfully via OAuth!`);
        fetchSocialAccounts();
        setTimeout(() => setSuccess(''), 3000);
      } else if (event.data.type === 'oauth-error') {
        const errorMsg = event.data.error === 'access_denied'
          ? `You cancelled ${event.data.platform} authorization. Please try again and grant permissions.`
          : `Failed to connect ${event.data.platform}. Please try again.`;
        setError(errorMsg);
        setTimeout(() => setError(''), 5000);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [fetchSocialAccounts]);

  const fetchOAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/oauth2/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setOauthStatus(response.data);
      }
    } catch (error) {
      // Error handled silently
    }
  };

  const platforms = [
    {
      id: 'instagram',
      name: 'Instagram',
      icon: Instagram,
      color: 'bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500',
      hoverColor: 'hover:from-purple-700 hover:via-pink-700 hover:to-orange-600',
      textColor: 'text-purple-600',
      description: 'Connect your Instagram account to showcase your content',
      metricLabel: 'Followers'
    },
    {
      id: 'youtube',
      name: 'YouTube',
      icon: Youtube,
      color: 'bg-red-600',
      hoverColor: 'hover:bg-red-700',
      textColor: 'text-red-600',
      description: 'Link your YouTube channel to display your videos',
      metricLabel: 'Subscribers'
    },
    {
      id: 'twitter',
      name: 'Twitter / X',
      icon: Twitter,
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      textColor: 'text-blue-500',
      description: 'Link your Twitter/X profile to showcase your tweets',
      metricLabel: 'Followers'
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: Facebook,
      color: 'bg-blue-700',
      hoverColor: 'hover:bg-blue-800',
      textColor: 'text-blue-700',
      description: 'Connect your Facebook page or profile',
      metricLabel: 'Followers'
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'bg-blue-600',
      hoverColor: 'hover:bg-blue-700',
      textColor: 'text-blue-600',
      description: 'Link your LinkedIn profile for professional connections',
      metricLabel: 'Connections'
    }
  ];

  const handleOAuthConnect = async (platform) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/oauth2/connect/${platform.id}`, {
        params: { userId },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.authorizationUrl) {
        // Open OAuth popup
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        const popup = window.open(
          response.data.authorizationUrl,
          `${platform.name} Login`,
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,resizable=yes`
        );

        // Check if popup was blocked
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          setError('Popup was blocked. Please allow popups for this site and try again.');
          setTimeout(() => setError(''), 5000);
        }
      } else {
        setError('OAuth is not configured for this platform. Please contact support.');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to initiate connection. Please try again.');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDisconnect = async (platformId) => {
    if (!window.confirm(`Are you sure you want to disconnect your ${platformId} account?`)) {
      return;
    }

    try {
      setConnectingPlatform(platformId);
      const token = localStorage.getItem('token');
      await api.delete(`/creators/${userId}/social-accounts/${platformId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSocialAccounts(prev => ({
        ...prev,
        [platformId]: { connected: false, username: '', followers: 0, url: '', oauthConnected: false }
      }));
      setSuccess(`${platformId} disconnected successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(`Failed to disconnect ${platformId}. Please try again.`);
      setTimeout(() => setError(''), 3000);
    } finally {
      setConnectingPlatform(null);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-collaborb-band4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Social Media Accounts</h2>
        <p className="text-gray-600">Connect your social media accounts to showcase your influence and reach</p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-800">
          <CheckCircle className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Platform Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {platforms.map((platform) => {
          const Icon = platform.icon;
          const account = socialAccounts[platform.id];
          const isConnected = account?.connected;

          return (
            <div
              key={platform.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Card Header */}
              <div className={`${platform.color} p-4 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{platform.name}</h3>
                      {isConnected && (
                        <div className="flex items-center gap-1 mt-1">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm">Connected</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4">
                {isConnected ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">Username</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">@{account.username}</p>
                        {account.url && (
                          <a
                            href={account.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${platform.textColor} hover:underline`}
                            title="View profile"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        {account.oauthConnected && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" title="Connected via OAuth">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Verified
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Instagram Engagement Metrics */}
                    {platform.id === 'instagram' && account.oauthConnected && account.engagementRate && (
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-3 rounded-lg space-y-2 border border-purple-100">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-xs text-purple-600 font-medium">Followers</p>
                            <p className="text-lg font-bold text-purple-900">{formatNumber(account.followers || 0)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-pink-600 font-medium">Engagement</p>
                            <p className="text-lg font-bold text-pink-900">{account.engagementRate}%</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-purple-200">
                          <div>
                            <p className="text-xs text-gray-600">Avg Likes</p>
                            <p className="text-sm font-semibold text-gray-900">{formatNumber(account.averageLikes || 0)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Avg Comments</p>
                            <p className="text-sm font-semibold text-gray-900">{formatNumber(account.averageComments || 0)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Posts</p>
                            <p className="text-sm font-semibold text-gray-900">{account.mediaCount || 0}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Regular follower display for non-Instagram or non-OAuth accounts */}
                    {!(platform.id === 'instagram' && account.oauthConnected && account.engagementRate) && (
                      <div>
                        <p className="text-sm text-gray-500">{platform.metricLabel}</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatNumber(account.followers || account.subscribers || account.connections || 0)}
                        </p>
                      </div>
                    )}
                    <div className="flex gap-2 pt-2">
                      {account.oauthConnected ? (
                        <>
                          <button
                            onClick={() => handleOAuthConnect(platform)}
                            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                            title="Reconnect to refresh data"
                          >
                            Reconnect
                          </button>
                          <button
                            onClick={() => handleDisconnect(platform.id)}
                            disabled={connectingPlatform === platform.id}
                            className="flex-1 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium disabled:opacity-50"
                          >
                            {connectingPlatform === platform.id ? 'Disconnecting...' : 'Disconnect'}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleOAuthConnect(platform)}
                            className={`flex-1 px-4 py-2 ${platform.color} ${platform.hoverColor} text-white rounded-lg transition-colors text-sm font-medium`}
                            title="Upgrade to OAuth connection"
                          >
                            Upgrade to OAuth
                          </button>
                          <button
                            onClick={() => handleDisconnect(platform.id)}
                            disabled={connectingPlatform === platform.id}
                            className="flex-1 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium disabled:opacity-50"
                          >
                            {connectingPlatform === platform.id ? 'Disconnecting...' : 'Disconnect'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">{platform.description}</p>

                    {/* OAuth Connect Button - Primary method */}
                    <button
                      onClick={() => handleOAuthConnect(platform)}
                      className={`w-full px-4 py-2 ${platform.color} ${platform.hoverColor} text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2 shadow-md hover:shadow-lg`}
                    >
                      <LinkIcon className="w-4 h-4" />
                      Sign In with {platform.name}
                    </button>

                    {/* Show manual option only if OAuth is not configured */}
                    {!oauthStatus[platform.id] && (
                      <p className="text-xs text-center text-gray-500 mt-2">
                        OAuth not configured. Please contact administrator.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SocialMediaManagement;


